import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './QuotationStyles.css';

const API = 'https://cctvapi.shopkwality.in/api';

const normalizeNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'object' && value.d) {
    const asString = value.d.join('') + 'e' + value.e;
    return Number(Number(asString).toFixed(decimals));
  }
  const num = Number(value);
  if (isNaN(num)) return 0;
  return Number(num.toFixed(decimals));
};

const ViewQuotation = () => {
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    fetchQuotation();
  }, [id]);

  const fetchQuotation = async () => {
    try {
      const res = await axios.get(`${API}/quotations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const q = res.data.data || res.data;
      setQuotation({
        ...q,
        charges: normalizeNumber(q.charges),
        discount: normalizeNumber(q.discount),
        subtotal: normalizeNumber(q.subtotal),
        gst_amount: normalizeNumber(q.gst_amount),
        total: normalizeNumber(q.total),
        items: q.items.map(i => ({
          ...i,
          quantity: normalizeNumber(i.quantity, 0),
          unit_price: normalizeNumber(i.unit_price),
          gst_percent: normalizeNumber(i.gst_percent ?? 18, 0),
          gst_amount: normalizeNumber(i.gst_amount),
          total_price: normalizeNumber(i.total_price)
        }))
      });
    } catch (err) {
      console.error(err);
      alert('Failed to load quotation');
      navigate('/quotations');
    } finally {
      setLoading(false);
    }
  };

  const generatePublicShare = async () => {
    setSharing(true);
    try {
      const res = await axios.post(
        `${API}/quotations/${id}/share`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { url } = res.data;
      
      // Copy to clipboard
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        alert(`Share link copied to clipboard!\n\n${url}`);
      } else {
        alert(`Share Link:\n\n${url}`);
      }
      
      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
      alert('Failed to generate share link');
    } finally {
      setSharing(false);
    }
  };

  const sendWhatsapp = async () => {
    setSharing(true);
    try {
      const res = await axios.get(
        `${API}/quotations/${id}/share/whatsapp`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { wa_link } = res.data;
      window.open(wa_link, '_blank');
    } catch (e) {
      console.error(e);
      alert('Failed to generate WhatsApp link');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="dashboard-main">
          <div className="loading-container">
            <div className="spinner large"></div>
            <p>Loading quotation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="dashboard-main">
          <div className="empty-state">
            <div className="empty-text">Quotation not found</div>
            <button onClick={() => navigate('/quotations')} className="btn btn-primary" style={{ marginTop: '20px' }}>
              Back to Quotations
            </button>
          </div>
        </div>
      </div>
    );
  }

  const customerName = quotation.customer?.name || quotation.customer_name || 'Customer';
  const customerMobile = quotation.customer?.mobile || quotation.customer_mobile || '';

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="page-header-quotation">
          <div className="container">
            <div className="quotation-header">
              <div>
                <h1 className="page-title">{quotation.quote_no}</h1>
                <p className="page-subtitle">
                  {customerName} {customerMobile && `• ${customerMobile}`}
                </p>
              </div>
              <span className={`status-badge status-${quotation.status}`}>
                {quotation.status.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        <div className="container">
          {/* Items */}
          <div className="quotation-section">
            <h2 className="section-title">Items</h2>
            <div className="view-items">
              {quotation.items.map((item, idx) => (
                <div key={idx} className="view-item-card">
                  <div className="item-main">
                    <h3 className="item-name">{item.product_name || 'Custom Product'}</h3>
                    <div className="item-details-grid">
                      <div className="item-detail">
                        <span className="detail-label">Quantity</span>
                        <span className="detail-value">{item.quantity}</span>
                      </div>
                      <div className="item-detail">
                        <span className="detail-label">Unit Price</span>
                        <span className="detail-value">₹{item.unit_price}</span>
                      </div>
                      <div className="item-detail">
                        <span className="detail-label">GST ({item.gst_percent}%)</span>
                        <span className="detail-value">₹{Math.round(item.gst_amount)}</span>
                      </div>
                      <div className="item-detail">
                        <span className="detail-label">Line Total</span>
                        <span className="detail-value total-highlight">₹{Math.round(item.total_price)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="quotation-summary">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>₹{Math.round(quotation.subtotal)}</span>
            </div>
            <div className="summary-row">
              <span>GST:</span>
              <span>₹{Math.round(quotation.gst_amount)}</span>
            </div>
            {quotation.charges > 0 && (
              <div className="summary-row">
                <span>Additional Charges:</span>
                <span>₹{quotation.charges}</span>
              </div>
            )}
            {quotation.discount > 0 && (
              <div className="summary-row">
                <span>Discount:</span>
                <span className="discount-value">-₹{quotation.discount}</span>
              </div>
            )}
            <div className="summary-row summary-total">
              <span>Grand Total:</span>
              <span>₹{Math.round(quotation.total)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="quotation-actions">
            <button 
              onClick={sendWhatsapp} 
              className="btn btn-whatsapp"
              disabled={sharing}
            >
              {sharing ? (
                <>
                  <span className="spinner"></span>
                  Generating...
                </>
              ) : (
                <>📱 Send via WhatsApp</>
              )}
            </button>
            <button 
              onClick={generatePublicShare} 
              className="btn btn-primary"
              disabled={sharing}
            >
              {sharing ? (
                <>
                  <span className="spinner"></span>
                  Generating...
                </>
              ) : (
                <>🔗 Generate Public Link</>
              )}
            </button>
            {quotation.status !== 'converted' && (
              <button 
                onClick={() => navigate(`/quotations/${id}/edit`)} 
                className="btn btn-secondary"
              >
                ✏️ Edit Quotation
              </button>
            )}
            <button 
              onClick={() => navigate('/quotations')} 
              className="btn btn-secondary"
            >
              ← Back to List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewQuotation;